import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { ChevronDownIcon } from '../../icons/ui-icons';

export interface NavMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Pending count — queued jobs, unread reports, open trade offers. */
  badge?: number;
  /** Draws attention in danger red — incoming attack, comet on course. */
  alert?: boolean;
  disabled?: boolean;
  /** Requirement note shown when disabled, e.g. "Ab Level 20". */
  lockedHint?: string;
}

export interface NavMenuProps {
  items: NavMenuItem[];
  value: string;
  onChange: (id: string) => void;
  /**
   * `rail` — the desktop left column from the wireframe.
   * `dock` — a fixed bottom bar for phones, which Capacitor ships as the app's
   *          primary navigation.
   */
  variant?: 'rail' | 'dock';
  className?: string;
}

/** How many items the dock shows before folding the rest behind "Mehr". */
const DOCK_PRIMARY_COUNT = 4;

interface NavButtonProps {
  item: NavMenuItem;
  active: boolean;
  isDock: boolean;
  onClick: () => void;
}

function NavButton({ item, active, isDock, onClick }: NavButtonProps) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      disabled={item.disabled}
      title={item.disabled ? item.lockedHint : undefined}
      data-tour-nav={item.id}
      onClick={onClick}
      className={cn(
        'group relative font-display uppercase tracking-[0.1em] transition-all',
        'duration-[var(--sg-duration-fast)] ease-hud',
        'disabled:pointer-events-none disabled:opacity-35',
        isDock
          ? 'flex flex-1 flex-col items-center gap-0.5 rounded-control px-1 py-1.5 text-label-sm'
          : 'flex items-center gap-2.5 rounded-control px-3 py-2 text-label',
        active
          ? 'bg-silicate/12 text-silicate-bright'
          : 'text-content-muted hover:bg-surface-3 hover:text-content',
      )}
    >
      {/* Active marker: a glowing edge bar on the rail, an underline in the dock. */}
      {active ? (
        <span
          aria-hidden
          className={cn(
            'absolute bg-silicate shadow-glow',
            isDock ? 'inset-x-2 top-0 h-0.5 rounded-pill' : 'inset-y-1.5 left-0 w-0.5 rounded-pill',
          )}
        />
      ) : null}

      {item.icon ? (
        <span className={cn('shrink-0', item.alert && 'text-danger animate-pulse-glow')}>{item.icon}</span>
      ) : null}

      <span className={cn('truncate', !isDock && 'flex-1 text-left')}>{item.label}</span>

      {item.badge ? (
        <span
          data-sg-numeric
          className={cn(
            'rounded-pill px-1.5 text-label-sm font-semibold',
            isDock && 'absolute right-1 top-0.5',
            item.alert ? 'bg-danger text-content' : 'bg-surface-raised text-content-secondary',
          )}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Primary navigation (GDD §4 wireframe, "MAIN NAVI").
 *
 * The same items drive both layouts so a section can never exist on desktop
 * but go missing in the mobile app. The dock has real width limits a phone
 * screen imposes, though: past `DOCK_PRIMARY_COUNT` items it folds the rest
 * behind a "Mehr" toggle that expands a second row directly above the dock —
 * still inside HudShell's fixed bottom bar, so it never scrolls away either.
 */
export function NavMenu({ items, value, onChange, variant = 'rail', className }: NavMenuProps) {
  const isDock = variant === 'dock';
  const [expanded, setExpanded] = useState(false);

  const needsOverflow = isDock && items.length > DOCK_PRIMARY_COUNT + 1;
  const primary = needsOverflow ? items.slice(0, DOCK_PRIMARY_COUNT) : items;
  const overflow = needsOverflow ? items.slice(DOCK_PRIMARY_COUNT) : [];
  const overflowActive = overflow.some((item) => item.id === value);
  const overflowAlert = overflow.some((item) => item.alert);
  const overflowBadge = overflow.reduce((sum, item) => sum + (item.badge ?? 0), 0);

  const select = (id: string) => {
    onChange(id);
    setExpanded(false);
  };

  return (
    <>
      {needsOverflow && expanded ? (
        <nav
          aria-label="Weitere Navigation"
          className="z-hud grid grid-cols-4 gap-0.5 border-t border-b border-edge bg-surface-1/95 px-1 py-1 backdrop-blur"
        >
          {overflow.map((item) => (
            <NavButton key={item.id} item={item} active={item.id === value} isDock onClick={() => select(item.id)} />
          ))}
        </nav>
      ) : null}

      <nav
        aria-label="Hauptnavigation"
        className={cn(
          isDock
            ? 'z-hud flex items-stretch gap-0.5 border-t border-edge bg-surface-1/95 px-1 py-1 backdrop-blur'
            : 'flex flex-col gap-1 p-2',
          className,
        )}
      >
        {primary.map((item) => (
          <NavButton key={item.id} item={item} active={item.id === value} isDock={isDock} onClick={() => select(item.id)} />
        ))}

        {needsOverflow ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label="Weitere Bereiche"
            data-tour="nav-more"
            onClick={() => setExpanded((e) => !e)}
            className={cn(
              'group relative flex flex-1 flex-col items-center gap-0.5 rounded-control px-1 py-1.5',
              'font-display text-label-sm uppercase tracking-[0.1em] transition-all',
              'duration-[var(--sg-duration-fast)] ease-hud',
              overflowActive || expanded
                ? 'bg-silicate/12 text-silicate-bright'
                : 'text-content-muted hover:bg-surface-3 hover:text-content',
            )}
          >
            <span
              className={cn(
                'shrink-0 transition-transform',
                expanded && 'rotate-180',
                overflowAlert && 'text-danger animate-pulse-glow',
              )}
            >
              <ChevronDownIcon size={17} />
            </span>
            <span className="truncate">Mehr</span>
            {overflowBadge ? (
              <span
                data-sg-numeric
                className={cn(
                  'absolute right-1 top-0.5 rounded-pill px-1.5 text-label-sm font-semibold',
                  overflowAlert ? 'bg-danger text-content' : 'bg-surface-raised text-content-secondary',
                )}
              >
                {overflowBadge > 99 ? '99+' : overflowBadge}
              </span>
            ) : null}
          </button>
        ) : null}
      </nav>
    </>
  );
}
